import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight, Trophy } from 'lucide-react';
import { RankHallSeasonPanel } from '@/components/ranks/RankHall';
import { useClipperRankHallQuery } from '@/generated/graphql';
import { CLIPPER_RANK_HALL_VIEWER } from '@/queries/ranks';

const HALL_PAGE_SIZE = 50;

/**
 * The current season's hall of fame, as shown on /rankings but without the
 * season picker or the lifetime tab. Omitting `seasonId` makes the backend
 * return the current season. Hidden while loading, on error, or when the
 * season has no entries — same policy as `CpmClipperLeaderboard`.
 */
export function CurrentSeasonHall() {
  const { t } = useTranslation();
  const { data, error } = useClipperRankHallQuery({
    variables: { offset: 0, limit: HALL_PAGE_SIZE },
    errorPolicy: 'all',
  });
  const { data: viewerData } = useQuery(CLIPPER_RANK_HALL_VIEWER);

  const season = data?.clipperRankHall?.season;
  if (error || !season || season.entries.length === 0) return null;

  const viewerMe = viewerData?.myData;
  const viewer = viewerMe
    ? { user_id: viewerMe.id, name: viewerMe.name, avatar_url: viewerMe.profile_picture ?? null }
    : null;

  return (
    <section data-testid="earn-season-hall" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Trophy className="w-5 h-5 text-primary" />
          {t('ranks.hall')}
        </h2>
        <Link
          to="/rankings"
          className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-2 hover:underline"
        >
          {t('ranks.fullRankingLink')}
          <ChevronRight className="w-4 h-4" aria-hidden="true" />
        </Link>
      </div>
      <RankHallSeasonPanel season={season} viewer={viewer} />
    </section>
  );
}
